/**
 * ScheduleEditor — create or edit one dispatch schedule (CONTRACT §2 nrd-schedules).
 *
 * Everything that decides what a client receives lives on this form: the shape, the
 * window, the cron, the filters, and — the one with teeth — `include_opt_in`, the
 * explicit list of call-content columns the client asked for (CONTRACT §6). Opt-in
 * columns ship *only* because they are listed here, and `never` columns cannot be
 * listed at all, so the form shows both lists rather than hiding the second one.
 *
 * Three rules this form enforces before the API ever sees the request:
 *
 *  1. §3 guardrail — a detailed shape over more than 3 days is refused here, with the
 *     same sentence the dispatcher would fail with, plus the fix (borrower shape).
 *  2. The cron is validated as the six-field EventBridge expression the API stores, and
 *     read back in English, so "30 7 ? * MON-FRI *" is confirmed as "Every Mon–Fri at
 *     07:30 IST" before it is saved. A schedule nobody can read is a schedule nobody
 *     notices is wrong — and one this form reads back *wrongly* is worse still, which is
 *     why the day-of-week numbering here is EventBridge's 1=Sunday, not ISO's 1=Monday.
 *  3. `client_id` is immutable after creation — SchedulePatch cannot carry it, and a
 *     schedule silently pointing at a different client is a data-leak shape, not a
 *     convenience.
 *  4. `recipient_ids` (CONTRACT-RECIPIENTS §1) names WHO gets it, and empty means
 *     everyone — that is today's behaviour, so no stored schedule changes meaning. There
 *     is no selection here that means nobody; that state is the Enabled switch. Switching
 *     client clears the selection, because a list of ids carried across clients is one
 *     lender's desks pointed at another lender's data, and the ids would not even resolve.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import AddRecipientsDialog from '@/components/AddRecipientsDialog';
import Badge, { PolicyBadge } from '@/components/Badge';
import Button, { ButtonGroup } from '@/components/Button';
import { Checkbox, Field, Input, Select, Switch } from '@/components/Field';
import Modal from '@/components/Modal';
import TestMailDialog from '@/components/TestMailDialog';
import { useToast } from '@/components/Toast';
import WindowPicker, { windowError } from '@/components/WindowPicker';
import { api, errorMessage, fetchAllPages } from '@/lib/api';
import {
  WEEKDAY_LABELS,
  WEEKDAY_VALUES,
  buildCron,
  cronError,
  describeCron,
  formatTime,
  parseCron,
} from '@/lib/cron';
import type { CronParts, Frequency } from '@/lib/cron';
import { formatCount, humaniseColumn, ordinal, pluralise } from '@/lib/format';
import { REPORT_SHAPES } from '@/lib/types';
import type {
  Client,
  Recipient,
  RecipientKind,
  ReportFormat,
  ReportShape,
  ReportWindow,
  Schedule,
  ScheduleCreate,
  SchedulePatch,
} from '@/lib/types';

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

/** The one-line note under the shape picker, per shape. */
const SHAPE_HINTS: Record<ReportShape, string> = {
  borrower: 'One row per account — the §5 rollup, best and latest disposition.',
  detailed: 'One row per call. Capped at 3 days by CONTRACT §3.',
  workbook:
    "The client's own workbook: Summary, Dialing Performance, Disposition, PTP, Segment " +
    'and Reasons, computed live over Borrower-wise and Call Dump. Replaces the ' +
    'borrower/detailed pair rather than joining it.',
};

export interface ScheduleEditorProps {
  open: boolean;
  /** Null/undefined creates a new schedule. */
  schedule?: Schedule | null;
  clients: Client[];
  /** Pre-selects the client on a new schedule, e.g. from a filtered list. */
  defaultClientId?: string;
  onClose: () => void;
  onSaved: (schedule: Schedule, mode: 'create' | 'edit') => void;
  /** Called after a flow refresh writes to the client row, so the picker re-reads it. */
  onClientsChanged?: () => void;
}

const EMPTY_WINDOW: ReportWindow = { type: 'yesterday' };

const HOURS = Array.from({ length: 24 }, (_, index) => index);
const MINUTES = Array.from({ length: 60 }, (_, index) => index);
const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, index) => index + 1);
const two = (value: number): string => (value < 10 ? `0${value}` : String(value));

/** Sentinel option value — never a flow name, which is why it carries a space. */
const CUSTOM_FLOW = '\u0000 other';

const KIND_LABEL: Record<RecipientKind, string> = { to: 'To', cc: 'Cc', bcc: 'Bcc' };

/**
 * The server sends `sendable` — active, not suppressed, not hard-bounced or complained —
 * and it is the authority. The fallback is here only so a row from an API build that
 * predates the field is not silently treated as undeliverable, which would draw a
 * perfectly good desk as disabled and unpickable.
 */
function isSendable(row: Recipient): boolean {
  return (
    row.sendable ??
    (row.active &&
      !row.suppressed_at &&
      row.bounce_state !== 'hard' &&
      row.bounce_state !== 'complained')
  );
}

/** Said in words, not only in colour — this is the reason a row cannot be picked. */
function whyNotSendable(row: Recipient): string {
  if (row.bounce_state === 'hard') return 'Hard bounce — the address does not exist';
  if (row.bounce_state === 'complained') return 'Marked a report as spam';
  if (row.suppressed_at) return 'Suppressed — mail to it is accepted and dropped';
  if (!row.active) return 'Inactive';
  return 'Not deliverable';
}

export function ScheduleEditor({
  open,
  schedule,
  clients,
  defaultClientId,
  onClose,
  onSaved,
  onClientsChanged,
}: ScheduleEditorProps) {
  const toast = useToast();
  const mode: 'create' | 'edit' = schedule ? 'edit' : 'create';

  const [clientId, setClientId] = useState('');
  const [name, setName] = useState('');
  const [shape, setShape] = useState<ReportShape>('borrower');
  const [reportWindow, setReportWindow] = useState<ReportWindow>(EMPTY_WINDOW);
  // When it fires. Held as picker values, not as an expression: an operator setting
  // 21:30 should not have to know that EventBridge wants six fields with a '?' in one of
  // the two day slots. `customCron` is the escape hatch for an expression the pickers
  // cannot draw — see parseCron.
  const [frequency, setFrequency] = useState<Frequency>('daily');
  const [hour, setHour] = useState(7);
  const [minute, setMinute] = useState(30);
  const [weekdays, setWeekdays] = useState<number[]>([2, 3, 4, 5, 6]);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [customCron, setCustomCron] = useState('30 7 * * ? *');
  // True once the operator has chosen "Other…": a flow the client has no ranking for is a
  // legitimate filter, so the free-text box stays reachable even when a list exists.
  const [workspace, setWorkspace] = useState('');
  const [flowCustom, setFlowCustom] = useState(false);
  const [flowsRefreshing, setFlowsRefreshing] = useState(false);
  const [flowsNote, setFlowsNote] = useState<string | null>(null);
  const [format, setFormat] = useState<ReportFormat>('csv');
  const [flow, setFlow] = useState('');
  const [campaign, setCampaign] = useState('');
  const [optIn, setOptIn] = useState<string[]>([]);
  const [enabled, setEnabled] = useState(true);
  // Who receives it. 'all' is stored as an empty list, which is what an untouched
  // schedule already holds — see the header note; this is not a migration.
  const [recipientMode, setRecipientMode] = useState<'all' | 'pick'>('all');
  const [recipientIds, setRecipientIds] = useState<string[]>([]);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientsError, setRecipientsError] = useState<string | null>(null);
  const [recipientsKey, setRecipientsKey] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Seed exactly once per opening. Keying on the schedule (not on `clients`, whose
  // array identity changes every time the parent refetches) is what stops a background
  // refresh from wiping half-typed input out from under the operator.
  const seedKey = open ? (schedule?.schedule_id ?? '__new__') : null;
  const seededKey = useRef<string | null>(null);

  /**
   * Open an existing schedule's expression in the pickers, or in the custom field when it
   * is something they cannot draw. The expression is never rewritten on the way in — an
   * operator editing the name of a schedule must not silently change when it fires.
   */
  const applyCron = (expression: string): void => {
    const parts = parseCron(expression);
    setCustomCron(expression);
    if (!parts) {
      setFrequency('custom');
      return;
    }
    setFrequency(parts.frequency);
    setHour(parts.hour);
    setMinute(parts.minute);
    if (parts.frequency === 'weekly') setWeekdays(parts.weekdays);
    if (parts.frequency === 'monthly') setDayOfMonth(parts.dayOfMonth);
  };

  useEffect(() => {
    if (!open || seedKey === null) {
      seededKey.current = null;
      return;
    }
    if (seededKey.current === seedKey) return;
    seededKey.current = seedKey;

    setSubmitError(null);
    setSaving(false);
    if (schedule) {
      setClientId(schedule.client_id);
      setName(schedule.name ?? '');
      setShape(schedule.report_shape);
      setReportWindow(schedule.window ?? EMPTY_WINDOW);
      applyCron(schedule.cron);
      setFormat(schedule.format ?? 'csv');
      setWorkspace(schedule.workspace ?? '');
      setFlow(schedule.flow ?? '');
      setFlowCustom(false);
      setCampaign(schedule.campaign_pattern ?? '');
      setOptIn(schedule.include_opt_in ?? []);
      // Absent and empty are the same thing on the wire, and both mean everyone.
      setRecipientIds(schedule.recipient_ids ?? []);
      setRecipientMode((schedule.recipient_ids?.length ?? 0) > 0 ? 'pick' : 'all');
      setEnabled(schedule.enabled);
      return;
    }
    setClientId(defaultClientId ?? clients[0]?.client_id ?? '');
    setName('');
    setShape('borrower');
    setReportWindow(EMPTY_WINDOW);
    setFrequency('daily');
    setHour(7);
    setMinute(30);
    setWeekdays([2, 3, 4, 5, 6]);
    setDayOfMonth(1);
    setCustomCron('30 7 * * ? *');
    setFormat('csv');
    setWorkspace('');
    setFlow('');
    setFlowCustom(false);
    setCampaign('');
    setOptIn([]);
    setRecipientIds([]);
    setRecipientMode('all');
    setEnabled(true);
  }, [open, seedKey, schedule, defaultClientId, clients]);

  const client = useMemo(
    () => clients.find((entry) => entry.client_id === clientId) ?? null,
    [clients, clientId],
  );

  // The shapes this client may be scheduled for. `workbook` is computed against one
  // client's own disposition vocabulary, so offering it everywhere would buy a schedule
  // that saves and then fails at 08:30 — the API refuses it on the same rule.
  const shapeOptions = useMemo(() => {
    const allowed = client?.report_shapes;
    // An older API sends no list. Falling back to "everything" would offer the workbook to
    // every client; falling back to the two defaults matches what that API enforces.
    const values = allowed?.length ? allowed : (['borrower', 'detailed'] as ReportShape[]);
    return REPORT_SHAPES.filter((entry) => values.includes(entry.value));
  }, [client]);

  // Switching client can strip the shape out from under the picker. Leaving it selected
  // would submit a shape the new client cannot use and surface as a save error.
  useEffect(() => {
    if (!open || !client) return;
    if (!shapeOptions.some((entry) => entry.value === shape)) setShape('borrower');
  }, [open, client, shapeOptions, shape]);

  // What the flow filter may offer, from three places, best evidence first:
  //
  //   1. OBSERVED — flows the client's own exports have actually contained. A scheduled
  //      run counts every row, so these are exact; a refresh samples and says so.
  //   2. CONFIGURED — flows with a ranking (CONTRACT-FLOWS §1). Usually a subset, but a
  //      campaign configured before its first export has no observation yet.
  //   3. The value this schedule already holds, always, or opening the editor on a flow
  //      that has since stopped appearing would silently widen the filter to every flow.
  //
  // This was a free-text box. Four schedules created on 21 Sep 2026 named `Aasha` and
  // `Roshini`; the only value Fusion's exports have ever carried is `Kiran`, and a flow
  // that matches nothing produces a report of zeroes rather than an error.
  const observedFlows = useMemo(
    () => (client?.observed_flows ?? []).map((entry) => entry.name).filter(Boolean),
    [client],
  );
  const flowOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const name of [...observedFlows, ...Object.keys(client?.flows ?? {})]) {
      const key = name.toLowerCase();
      if (name && !seen.has(key)) {
        seen.add(key);
        out.push(name);
      }
    }
    const current = flow.trim();
    if (current && !seen.has(current.toLowerCase())) out.push(current);
    return out;
  }, [client, flow, observedFlows]);

  const flowRows = useMemo(
    () => new Map((client?.observed_flows ?? []).map((entry) => [entry.name, entry])),
    [client],
  );

  const refreshFlows = async () => {
    if (!clientId || flowsRefreshing) return;
    setFlowsRefreshing(true);
    setFlowsNote(null);
    try {
      const result = await api.clients.refreshFlows(clientId);
      onClientsChanged?.();
      const names = (result.flows ?? []).map((entry) => entry.name);
      setFlowsNote(
        names.length
          ? `${pluralise(names.length, 'flow')} found: ${names.slice(0, 6).join(', ')}${
              names.length > 6 ? '…' : ''
            }`
          : 'No campaign names found in the exports we could read.',
      );
    } catch (err) {
      setFlowsNote(err instanceof Error ? err.message : 'Could not read the exports.');
    } finally {
      setFlowsRefreshing(false);
    }
  };

  const optInOptions = useMemo(() => client?.column_policy?.opt_in ?? [], [client]);
  const neverColumns = useMemo(() => client?.column_policy?.never ?? [], [client]);

  // A selection the newly-chosen client does not offer must not survive the switch.
  // Guarded on `client`: while the client list is still loading the policy is unknown,
  // and pruning against an empty list would silently strip an existing schedule's
  // opt-in columns — the client would quietly stop receiving columns they asked for.
  useEffect(() => {
    if (!client) return;
    setOptIn((current) => {
      const kept = current.filter((column) => optInOptions.includes(column));
      return kept.length === current.length ? current : kept;
    });
  }, [client, optInOptions]);

  /**
   * The client's recipients, for the picker and for the readback of who "all" resolves
   * to. Loaded here rather than passed in: the parent's client list carries no
   * recipients, and a schedule editor that showed a stale list would be showing a
   * delivery that is not the one about to happen.
   */
  useEffect(() => {
    if (!open || !clientId) {
      setRecipients([]);
      return;
    }
    let cancelled = false;
    setRecipientsLoading(true);
    setRecipientsError(null);
    fetchAllPages<Recipient>((cursor) =>
      api.recipients.list(clientId, cursor ? { cursor, limit: 100 } : { limit: 100 }),
    )
      .then((items) => {
        if (!cancelled) setRecipients(items);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Empty the list rather than leave the previous client's addresses on screen —
        // that is the one wrong thing this panel could show.
        setRecipients([]);
        setRecipientsError(errorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setRecipientsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, clientId, recipientsKey]);

  const cron = useMemo(() => {
    if (frequency === 'custom') return customCron.trim();
    const parts: CronParts = { frequency, hour, minute, weekdays, dayOfMonth };
    return buildCron(parts);
  }, [frequency, hour, minute, weekdays, dayOfMonth, customCron]);

  // A weekly schedule with no day selected builds an expression with an empty day-of-week
  // field. Say what to do about it rather than letting cronError describe the symptom.
  const cronProblem =
    frequency === 'weekly' && weekdays.length === 0
      ? 'Pick at least one day of the week.'
      : cronError(cron);
  const cronReading = describeCron(cron);
  const windowProblem = windowError(reportWindow, shape);
  const clientProblem = clientId ? null : 'Pick the client this schedule dispatches for.';
  /**
   * "Only the ones I pick", with nobody picked, saved `recipient_ids: []` — and an empty
   * list means *every* active recipient (CONTRACT-RECIPIENTS §1). So the one selection
   * that reads as "send to nobody but these" silently meant the opposite: the whole
   * client. The screen even said "Nothing would be delivered" while about to widen.
   *
   * The ambiguity is inherent in the wire format and is not worth changing — empty-means-
   * all is what makes every pre-existing schedule keep its meaning. So the form must never
   * produce that state from this branch.
   */
  const recipientProblem =
    recipientMode === 'pick' && recipientIds.length === 0
      ? 'Pick at least one recipient, or switch to “All active recipients”. Saving no picks would send to everyone.'
      : null;

  const blocking = clientProblem ?? cronProblem ?? windowProblem ?? recipientProblem;

  // Sorted the way the dispatcher resolves them (to, then cc, then bcc, then address), so
  // the order here is the order a run reports — not the order DynamoDB happened to return.
  const orderedRecipients = useMemo(() => {
    const rank: Record<RecipientKind, number> = { to: 0, cc: 1, bcc: 2 };
    return [...recipients].sort(
      (a, b) =>
        (rank[a.kind] ?? 3) - (rank[b.kind] ?? 3) ||
        a.email.toLowerCase().localeCompare(b.email.toLowerCase()),
    );
  }, [recipients]);

  const selectedSet = useMemo(() => new Set(recipientIds), [recipientIds]);

  /**
   * Ids on the schedule that this client no longer has a row for. Never pruned quietly:
   * a schedule that mails fewer people than it lists is exactly the failure the contract
   * refuses to allow, so it is named and removed on purpose or not at all. Held back
   * while the list is loading or failed, where "not found" means "not fetched".
   */
  const unknownIds = useMemo(() => {
    if (recipientsLoading || recipientsError) return [];
    const known = new Set(recipients.map((row) => row.recipient_id));
    return recipientIds.filter((id) => !known.has(id));
  }, [recipientIds, recipients, recipientsLoading, recipientsError]);

  /** Who this schedule would actually mail, as configured right now. */
  const effectiveRecipients = useMemo(
    () =>
      recipientMode === 'all'
        ? orderedRecipients.filter(isSendable)
        : orderedRecipients.filter((row) => selectedSet.has(row.recipient_id) && isSendable(row)),
    [recipientMode, orderedRecipients, selectedSet],
  );

  const allPicked =
    orderedRecipients.length > 0 &&
    orderedRecipients.every((row) => !isSendable(row) || selectedSet.has(row.recipient_id));

  const toggleRecipient = (recipientId: string): void => {
    setRecipientIds((current) =>
      current.includes(recipientId)
        ? current.filter((entry) => entry !== recipientId)
        : [...current, recipientId],
    );
  };

  const allOptInSelected =
    optInOptions.length > 0 && optInOptions.every((column) => optIn.includes(column));

  const toggleOptIn = (column: string): void => {
    setOptIn((current) =>
      current.includes(column) ? current.filter((entry) => entry !== column) : [...current, column],
    );
  };

  const save = async (): Promise<void> => {
    if (blocking || saving) return;
    setSaving(true);
    setSubmitError(null);
    try {
      const shared = {
        report_shape: shape,
        window: reportWindow,
        workspace: workspace.trim() || null,
        flow: flow.trim() || null,
        campaign_pattern: campaign.trim() || null,
        cron: cron.trim(),
        include_opt_in: client ? optIn.filter((column) => optInOptions.includes(column)) : optIn,
        // 'all' is an empty list, not a list of everyone: a frozen copy of today's
        // recipients would quietly stop including whoever is added tomorrow.
        recipient_ids: recipientMode === 'pick' ? recipientIds : [],
        // The workbook generator writes an xlsx whatever this says, so submitting the
        // picker's leftover 'csv' would store a format the run then contradicts.
        format: shape === 'workbook' ? ('xlsx' as ReportFormat) : format,
        enabled,
        name: name.trim() || undefined,
      };

      if (schedule) {
        const patch: SchedulePatch = shared;
        const updated = await api.schedules.patch(schedule.schedule_id, patch);
        toast.success('Schedule saved', {
          description: cronReading ? `${cronReading}.` : undefined,
        });
        onSaved(updated, 'edit');
      } else {
        const body: ScheduleCreate = { client_id: clientId, ...shared };
        const created = await api.schedules.create(body);
        toast.success('Schedule created', {
          description: enabled
            ? `${cronReading ?? 'It'} — it will fire on that cron.`
            : 'Created disabled; nothing will be dispatched until you enable it.',
        });
        onSaved(created, 'create');
      }
      onClose();
    } catch (err) {
      setSubmitError(errorMessage(err));
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        // Escape and a backdrop click are disarmed while a child dialog is open: both
        // modals listen on document, so one Escape would otherwise close this form too and
        // take half-typed input with it.
        dismissible={!saving && !addOpen && !testOpen}
        hideClose={saving}
        size="xl"
        // The lender whose borrower data this form governs is named in the title. On an
        // unnamed schedule the title used to read exactly "Edit schedule", so an operator
        // who opened the wrong row could not tell from anything above the fold.
        title={
          mode === 'create'
            ? 'New schedule'
            : `${client?.name ?? clientId} · ${schedule?.name ?? schedule?.schedule_id ?? ''}`
        }
        description={
          mode === 'create'
            ? 'What gets built, for whom, and when it fires. All times are IST.'
            : 'Changes apply to the next fire. They do not alter runs that already went out.'
        }
        footer={
          <>
            {/* The control that decides whether this schedule fires at all used to be the
                third cell of the Frequency row, drawn exactly like the minute picker. In
                the footer it is on screen the whole time the form is open. */}
            <Switch
              checked={enabled}
              disabled={saving}
              onChange={setEnabled}
              label={enabled ? 'Schedule is live' : 'Schedule is off'}
            />
            <Button
              variant="ghost"
              disabled={saving || !schedule}
              title={
                schedule
                  ? 'Sends this schedule’s real report to one internal address. It tests the SAVED schedule — save first if you have changed anything.'
                  : 'Create the schedule first. A disabled schedule costs nothing, and it is the thing that will actually fire.'
              }
              onClick={() => setTestOpen(true)}
            >
              Send a test
            </Button>
            {/* Why the primary is disabled, in words, beside the button rather than in a
                tooltip on it: a title is invisible on a keyboard, on touch and in a
                screenshot. It is not truncated — two of the four blocking reasons are
                contract paragraphs, and the §3 refusal is the longest of them. */}
            {blocking ? (
              // role="alert" so it is announced when it appears, and a glyph so the reason
              // is not carried by colour alone — the same rule the badges follow. Danger
              // prose with neither reads as ordinary text to anyone who cannot see the hue.
              <span
                role="alert"
                className="small grow"
                style={{ color: 'var(--danger-text)', minWidth: 0, textAlign: 'right' }}
              >
                <span aria-hidden="true">✕ </span>
                {blocking}
              </span>
            ) : (
              <span className="grow" />
            )}
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={saving}
              disabled={Boolean(blocking)}
              title={blocking ?? undefined}
              onClick={() => void save()}
            >
              {mode === 'create' ? 'Create schedule' : 'Save changes'}
            </Button>
          </>
        }
      >
        <div className="stack">
          {/* ------------------------------------------------------- identity */}
          <div
            className="field-row"
            // Explicit tracks, not auto-fit: a name box does not need the same width as
            // the client it belongs to, and auto-fit handed every field an equal share
            // regardless of what it held.
            style={{ gridTemplateColumns: 'minmax(260px, 2fr) minmax(200px, 1fr)' }}
          >
            <Field
              label="Client"
              required
              hint={
                mode === 'edit'
                  ? 'Fixed after creation — a schedule cannot be repointed at another client.'
                  : client
                    ? `${client.company_name} · ${client.s3_prefix}`
                    : 'Reads from that client’s prefix under s3://neuron-report/'
              }
            >
              {mode === 'edit' ? (
                // A disabled dropdown is the wrong shape for a value that can never
                // change, and it draws the one immutable fact on the form in the colour
                // the console uses for unavailable controls.
                <div
                  className="cluster-tight"
                  style={{ minHeight: 'var(--control-h)', flexWrap: 'nowrap', minWidth: 0 }}
                >
                  <span className="mono dim" aria-hidden="true" title="Fixed after creation">
                    ⊠
                  </span>
                  <span className="strong truncate">{client?.name ?? clientId}</span>
                  <span className="xs dim mono truncate">
                    {clientId}
                    {/* The lender only when it is a different string from the name;
                        on most clients the two are the same and the second copy is noise. */}
                    {client?.company_name && client.company_name !== client.name
                      ? ` · ${client.company_name}`
                      : ''}
                  </span>
                </div>
              ) : (
                (control) => (
                  <Select
                    {...control}
                    value={clientId}
                    disabled={saving || clients.length === 0}
                    onChange={(event) => {
                      // Switching client clears the picks: the ids belong to the previous
                      // client's rows, and carrying them would point one lender's desks at
                      // another lender's data. This lives on the control rather than in an
                      // effect on `clientId` — an effect declared after the seed one reads
                      // the PREVIOUS render's clientId, so opening an existing schedule
                      // looked like a client switch and wiped the ids the seed had just
                      // applied. `client_id` is immutable on edit, so this only fires while
                      // creating.
                      setClientId(event.target.value);
                      setRecipientIds([]);
                      setRecipientMode('all');
                    }}
                  >
                    {clients.length === 0 ? <option value="">No clients loaded</option> : null}
                    {clients.map((entry) => (
                      <option key={entry.client_id} value={entry.client_id}>
                        {entry.name}
                        {entry.active ? '' : ' (inactive)'}
                      </option>
                    ))}
                  </Select>
                )
              )}
            </Field>

            <Field label="Name" optional hint="Shown in the schedule list and in run history.">
              {(control) => (
                <Input
                  {...control}
                  value={name}
                  disabled={saving}
                  placeholder="Morning collections summary"
                  onChange={(event) => setName(event.target.value)}
                />
              )}
            </Field>
          </div>

          {/* ----------------------------------------------------- recipients
              Second on the form, not last. This decides which mailboxes receive a
              lender's borrower data (CONTRACT-RECIPIENTS §1); it used to open below the
              fold, under a file-extension preference. */}
          <section className="stack-tight">
            <div className="cluster">
              <span className="eyebrow">Recipients</span>
              <span className="small dim">
                {recipientsLoading
                  ? 'Reading this client’s list…'
                  : recipientMode === 'all'
                    ? `${pluralise(effectiveRecipients.length, 'deliverable recipient')} today`
                    : `${recipientIds.length} picked · ${pluralise(effectiveRecipients.length, 'deliverable recipient')}`}
              </span>
              <span className="cluster" style={{ gap: 'var(--s-1)', marginLeft: 'auto' }}>
                {recipientMode === 'pick' ? (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={saving || allPicked || orderedRecipients.length === 0}
                      title="Tick every deliverable recipient this client has"
                      onClick={() =>
                        setRecipientIds(
                          orderedRecipients.filter(isSendable).map((row) => row.recipient_id),
                        )
                      }
                    >
                      Select all
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={saving || recipientIds.length === 0}
                      onClick={() => setRecipientIds([])}
                    >
                      Clear
                    </Button>
                  </>
                ) : null}
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={saving || !clientId}
                  title={`Add an address to ${client?.name ?? clientId} — it is created on the client, not on this schedule`}
                  onClick={() => setAddOpen(true)}
                >
                  Add address
                </Button>
              </span>
            </div>

            <Field
              label="Who receives this report"
              hint={
                recipientMode === 'all'
                  ? 'Everyone active on this client, including anyone added later. This is what a schedule does when nobody picks.'
                  : 'Only the addresses ticked below. Someone added to this client afterwards will not be included until you tick them here.'
              }
            >
              <ButtonGroup label="Who receives this report">
                <Button
                  size="sm"
                  disabled={saving}
                  aria-pressed={recipientMode === 'all'}
                  className={recipientMode === 'all' ? 'is-selected' : ''}
                  onClick={() => setRecipientMode('all')}
                >
                  All active recipients
                </Button>
                <Button
                  size="sm"
                  disabled={saving}
                  aria-pressed={recipientMode === 'pick'}
                  className={recipientMode === 'pick' ? 'is-selected' : ''}
                  onClick={() => setRecipientMode('pick')}
                >
                  Only the ones I pick
                </Button>
              </ButtonGroup>
            </Field>

            {recipientsError ? (
              <div className="callout tone-warn" role="alert">
                <span className="callout-glyph" aria-hidden="true">
                  ▲
                </span>
                <span>
                  Could not read this client’s recipients, so this panel cannot show who would be
                  mailed. {recipientsError}{' '}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setRecipientsKey((key) => key + 1)}
                  >
                    Retry
                  </Button>
                </span>
              </div>
            ) : null}

            {recipientsLoading ? (
              // Never draw "goes to 0 recipients" over a list that has not arrived: an
              // operator who reads that and ticks their way out of it has just turned an
              // "everyone" schedule into a frozen subset.
              <div className="small dim">Reading {client?.name ?? clientId}’s recipients…</div>
            ) : !recipientsError && orderedRecipients.length === 0 ? (
              <div className="callout tone-danger">
                <span className="callout-glyph" aria-hidden="true">
                  ✕
                </span>
                <span>
                  <strong>{client?.name ?? clientId} has no recipients.</strong> This schedule would
                  build the report and reach nobody. Add an address before enabling it.
                </span>
              </div>
            ) : recipientMode === 'all' ? (
              // A readback of who gets it is a value, not an alert, so it carries no box.
              // The addresses are never truncated: this panel exists to confirm exactly
              // which mailbox receives a lender's borrower data, and a 47-character desk
              // address was being cut at 36 with the rest on a hover.
              <div className="readback stack-tight">
                <span>
                  Goes to <span className="strong">{pluralise(effectiveRecipients.length, 'recipient')}</span>{' '}
                  right now, and to whoever is active on {client?.name ?? clientId} on the day it
                  fires.
                </span>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '0 var(--s-4)',
                  }}
                >
                  {effectiveRecipients.map((row) => (
                    <span key={row.recipient_id} className="xs" style={{ overflowWrap: 'anywhere' }}>
                      <span className="mono strong">{row.email}</span>
                      <span className="dim"> · {KIND_LABEL[row.kind]}</span>
                    </span>
                  ))}
                </div>
                {orderedRecipients.length > effectiveRecipients.length ? (
                  <span className="xs dim">
                    {orderedRecipients.length - effectiveRecipients.length} configured but not
                    deliverable — inactive, suppressed or bounced — and skipped at send time.
                  </span>
                ) : null}
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: 'var(--s-1) var(--s-4)',
                }}
              >
                {orderedRecipients.map((row) => {
                  const picked = selectedSet.has(row.recipient_id);
                  const deliverable = isSendable(row);
                  return (
                    <Checkbox
                      key={row.recipient_id}
                      checked={picked}
                      // A row that has since bounced but is already on this schedule stays
                      // tickable, or the operator could see the problem and not remove it.
                      disabled={saving || (!deliverable && !picked)}
                      onChange={() => toggleRecipient(row.recipient_id)}
                      label={
                        <span className="mono" style={{ overflowWrap: 'anywhere' }}>
                          {row.email}
                        </span>
                      }
                      hint={
                        deliverable ? (
                          <>
                            {KIND_LABEL[row.kind]}
                            {row.name ? ` · ${row.name}` : ''}
                          </>
                        ) : (
                          <span className="cluster-tight">
                            <Badge tone="danger" glyph="⊘" terminalBad title={whyNotSendable(row)}>
                              not deliverable
                            </Badge>
                            <span>{whyNotSendable(row)}</span>
                          </span>
                        )
                      }
                    />
                  );
                })}
              </div>
            )}

            {!recipientsLoading &&
            !recipientsError &&
            orderedRecipients.length > 0 &&
            effectiveRecipients.length === 0 ? (
              <div className="callout tone-danger">
                <span className="callout-glyph" aria-hidden="true">
                  ✕
                </span>
                <span>
                  <strong>Nothing would be delivered.</strong>{' '}
                  {recipientMode === 'pick' && recipientIds.length === 0
                    ? 'Nobody is picked. An empty pick list means every active recipient, so this cannot be saved — pick at least one, or switch to “All active recipients”.'
                    : recipientMode === 'pick'
                      ? 'None of the picked addresses is deliverable. A schedule that resolves to zero recipients is a fault, not a configuration — if this report should stop going out, switch Enabled off instead.'
                      : 'Every recipient on this client is inactive, suppressed or bounced.'}
                </span>
              </div>
            ) : null}

            {unknownIds.length > 0 ? (
              <div className="callout tone-danger" role="alert">
                <span className="callout-glyph" aria-hidden="true">
                  ✕
                </span>
                <div className="stack-tight" style={{ minWidth: 0 }}>
                  <span>
                    <strong>
                      {pluralise(unknownIds.length, 'address', 'addresses')} on this schedule no
                      longer exist
                      {unknownIds.length === 1 ? 's' : ''} on {client?.name ?? clientId}.
                    </strong>{' '}
                    They are kept, not dropped quietly — a schedule that mails fewer people than it
                    lists is the thing this refuses to do silently. Remove them here, or restore the
                    recipient on the client.
                  </span>
                  {/* One id per line: this is a work list, and it used to be joined into
                      one clipped line with no title, so past the first two the value was
                      unrecoverable by any means. */}
                  <div className="stack-tight">
                    {unknownIds.map((id) => (
                      <span key={id} className="mono xs" style={{ overflowWrap: 'anywhere' }}>
                        {id}
                      </span>
                    ))}
                  </div>
                  <span>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={saving}
                      onClick={() =>
                        setRecipientIds((current) => current.filter((id) => !unknownIds.includes(id)))
                      }
                    >
                      Remove {unknownIds.length === 1 ? 'it' : 'them'} from this schedule
                    </Button>
                  </span>
                </div>
              </div>
            ) : null}
          </section>

          {/* ------------------------------------------------ window and shape */}
          <section className="stack-tight">
            <span className="eyebrow">What it reads</span>
            <div className="field-row" style={{ gridTemplateColumns: 'auto', justifyContent: 'start' }}>
              <Field label="Report shape" hint={SHAPE_HINTS[shape]}>
                <ButtonGroup label="Report shape">
                  {shapeOptions.map((entry) => (
                    <Button
                      key={entry.value}
                      size="sm"
                      disabled={saving}
                      aria-pressed={shape === entry.value}
                      className={shape === entry.value ? 'is-selected' : ''}
                      title={entry.hint}
                      onClick={() => setShape(entry.value)}
                    >
                      {entry.label}
                    </Button>
                  ))}
                </ButtonGroup>
              </Field>
            </div>

            <WindowPicker
              value={reportWindow}
              onChange={setReportWindow}
              shape={shape}
              onShapeChange={setShape}
              disabled={saving}
            />
          </section>

          {/* -------------------------------------------------- opt-in columns */}
          <section className="stack-tight">
            <div className="cluster">
              <span className="eyebrow">Opt-in columns</span>
              <PolicyBadge tier="opt_in" />
              <span className="small dim">
                {optIn.length === 0
                  ? 'None selected — only default columns ship.'
                  : `${pluralise(optIn.length, 'column')} selected`}
              </span>
              {optInOptions.length > 0 ? (
                <span className="cluster" style={{ gap: 'var(--s-1)', marginLeft: 'auto' }}>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={saving || allOptInSelected}
                    onClick={() => setOptIn([...optInOptions])}
                    title={`Select all ${optInOptions.length} opt-in columns`}
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={saving || optIn.length === 0}
                    onClick={() => setOptIn([])}
                  >
                    Clear
                  </Button>
                </span>
              ) : null}
            </div>

            {/* Standing policy, not a state: it is the same paragraph on every open, so
                as an amber box it taught the eye that amber is furniture — 30px above the
                one place a genuine warning would have to appear. The words are unchanged. */}
            <div className="field-hint">
              Anything derived from what was said on the call is opt-in, however short — a
              55-character summary is the same class as a full transcript, and recording links
              count too. Selected columns are still suppressed row-by-row where the borrower was
              not the counterparty (CONTRACT §6 right-party gate).
            </div>

            {optInOptions.length === 0 ? (
              <div className="small dim">
                {client
                  ? `${client.name} has no opt-in columns configured.`
                  : 'Pick a client to see the columns it can release.'}
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
                  gap: 'var(--s-1) var(--s-4)',
                }}
              >
                {optInOptions.map((column) => (
                  <Checkbox
                    key={column}
                    checked={optIn.includes(column)}
                    disabled={saving}
                    onChange={() => toggleOptIn(column)}
                    label={<span className="mono">{column}</span>}
                    hint={humaniseColumn(column)}
                  />
                ))}
              </div>
            )}

            {neverColumns.length > 0 ? (
              <details>
                <summary className="small dim" style={{ cursor: 'pointer' }}>
                  {pluralise(neverColumns.length, 'column')} are unreachable by any selection
                </summary>
                <div className="cluster" style={{ marginTop: 'var(--s-2)', gap: 'var(--s-1)' }}>
                  {neverColumns.map((column) => (
                    <span key={column} className="mono xs dim" title="Policy: never">
                      {column}
                    </span>
                  ))}
                </div>
                <div className="small dim" style={{ marginTop: 'var(--s-2)' }}>
                  Conduct-QA fields such as <span className="mono">critical_breaches</span> are our own
                  agent’s defect record, not a client deliverable. They never ship.
                </div>
              </details>
            ) : null}
          </section>

          {/* --------------------------------------------------- when it fires */}
          <section className="stack-tight">
            <span className="eyebrow">When it fires</span>
            <div
              className="field-row"
              // The time pickers are two 5.5rem selects; an equal share of the row gave
              // them 239px of dead space beside them. Tracks are packed to the left so
              // the time sits against the frequency it belongs to, not at the far edge
              // of a 1040px dialog.
              style={{ gridTemplateColumns: 'minmax(200px, 300px) auto', justifyContent: 'start' }}
            >
              <Field
                label="Frequency"
                hint={
                  frequency === 'custom'
                    ? 'A cron expression, for a schedule the pickers cannot express.'
                    : 'How often this report is built and sent.'
                }
              >
                {(control) => (
                  <Select
                    {...control}
                    value={frequency}
                    disabled={saving}
                    onChange={(event) => setFrequency(event.target.value as Frequency)}
                  >
                    <option value="daily">Every day</option>
                    <option value="weekly">Every week</option>
                    <option value="monthly">Every month</option>
                    <option value="custom">Custom…</option>
                  </Select>
                )}
              </Field>

              {frequency === 'custom' ? (
                <Field
                  label="Cron (IST)"
                  required
                  error={cronProblem}
                  hint={
                    cronReading
                      ? `${cronReading} · minute hour day-of-month month day-of-week year`
                      : 'minute hour day-of-month month day-of-week year — e.g. 30 7 ? * MON-FRI *'
                  }
                >
                  {(control) => (
                    <Input
                      {...control}
                      mono
                      value={customCron}
                      disabled={saving}
                      spellCheck={false}
                      autoComplete="off"
                      invalid={Boolean(cronProblem)}
                      onChange={(event) => setCustomCron(event.target.value)}
                    />
                  )}
                </Field>
              ) : (
                <Field
                  label="Time (IST)"
                  hint={`24-hour clock. Fires at ${formatTime(hour, minute)} India time.`}
                >
                  {(control) => (
                    <div className="cluster" style={{ gap: 'var(--s-1)', alignItems: 'center' }}>
                      <Select
                        {...control}
                        className="select-auto"
                        value={String(hour)}
                        disabled={saving}
                        aria-label="Hour"
                        style={{ width: '5.5rem' }}
                        onChange={(event) => setHour(Number(event.target.value))}
                      >
                        {HOURS.map((value) => (
                          <option key={value} value={value}>
                            {two(value)}
                          </option>
                        ))}
                      </Select>
                      <span className="dim" aria-hidden="true">
                        :
                      </span>
                      <Select
                        className="select-auto"
                        value={String(minute)}
                        disabled={saving}
                        aria-label="Minute"
                        style={{ width: '5.5rem' }}
                        onChange={(event) => setMinute(Number(event.target.value))}
                      >
                        {MINUTES.map((value) => (
                          <option key={value} value={value}>
                            {two(value)}
                          </option>
                        ))}
                      </Select>
                    </div>
                  )}
                </Field>
              )}
            </div>

            {frequency === 'weekly' ? (
              <Field label="Days" error={cronProblem} hint="Fires on each day selected.">
                <ButtonGroup label="Days of the week">
                  {WEEKDAY_VALUES.map((value) => {
                    const on = weekdays.includes(value);
                    return (
                      <Button
                        key={value}
                        size="sm"
                        disabled={saving}
                        aria-pressed={on}
                        className={on ? 'is-selected' : ''}
                        onClick={() =>
                          setWeekdays((current) =>
                            current.includes(value)
                              ? current.filter((entry) => entry !== value)
                              : [...current, value],
                          )
                        }
                      >
                        {WEEKDAY_LABELS[value - 1]}
                      </Button>
                    );
                  })}
                </ButtonGroup>
              </Field>
            ) : null}

            {frequency === 'monthly' ? (
              <div className="field-row" style={{ gridTemplateColumns: 'auto', justifyContent: 'start' }}>
                <Field
                  label="Day of the month"
                  hint={
                    dayOfMonth > 28
                      ? `Months without a ${ordinal(dayOfMonth)} are skipped — no report those months.`
                      : 'The same date every month.'
                  }
                >
                  {(control) => (
                    <Select
                      {...control}
                      className="select-auto"
                      value={String(dayOfMonth)}
                      disabled={saving}
                      style={{ width: '8rem' }}
                      onChange={(event) => setDayOfMonth(Number(event.target.value))}
                    >
                      {DAYS_OF_MONTH.map((value) => (
                        <option key={value} value={value}>
                          {ordinal(value)}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              </div>
            ) : null}

            {/* The one line an operator actually checks before saving. Shown for the pickers
                too, not only for a hand-written expression: it is the readback that catches a
                21:30 meant as 09:30, and it is generated from the expression that will be
                stored, never from the control values. A readback is a value, not an alert,
                so it is plain text — the boxed version stacked with two others of identical
                geometry and took 18% of the body between them. */}
            {cronReading && !cronProblem ? (
              <div className="readback">
                <span className="strong">{cronReading}</span>
                <span className="mono xs dim" style={{ marginLeft: 'var(--s-2)' }}>
                  {cron}
                </span>
              </div>
            ) : null}
          </section>

          {/* -------------------------------------------------------- filters */}
          <section className="stack-tight">
            <span className="eyebrow">Filters</span>
            <div
              className="field-row"
              style={{ gridTemplateColumns: 'repeat(2, minmax(200px, 1fr)) minmax(140px, 0.6fr)' }}
            >
              {client?.is_multi_workspace ? (
                <Field
                  label="Workspace"
                  optional
                  hint={
                    workspace
                      ? 'Only this workspace is read. The others are not in the report.'
                      : 'Every workspace this client exports from. A workspace with no export for the window is named in the file, not skipped.'
                  }
                >
                  {(control) => (
                    <Select
                      {...control}
                      value={workspace}
                      disabled={saving}
                      onChange={(event) => setWorkspace(event.target.value)}
                    >
                      <option value="">All workspaces</option>
                      {(client?.workspaces ?? []).map((entry) => (
                        <option key={entry.key} value={entry.key}>
                          {entry.name}
                          {entry.active === false ? ' — parked' : ''}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              ) : null}
              <Field
                label="Flow filter"
                optional
                hint={
                  flowsNote
                    ? flowsNote
                    : flowOptions.length > 0
                      ? `Matched against ${client?.field_map?.flow ?? 'the flow column'}, case-insensitively.`
                      : client?.flows_refreshed_at
                        ? 'No campaign names found in this client’s exports. Blank means every flow.'
                        : 'Nobody has read this client’s exports yet — refresh to see what they contain.'
                }
              >
                {(control) =>
                  flowOptions.length > 0 && !flowCustom ? (
                    <Select
                      {...control}
                      value={flow}
                      disabled={saving}
                      onChange={(event) => {
                        if (event.target.value === CUSTOM_FLOW) {
                          setFlowCustom(true);
                          setFlow('');
                          return;
                        }
                        setFlow(event.target.value);
                      }}
                    >
                      <option value="">All flows</option>
                      {flowOptions.map((entry) => {
                        const seen = flowRows.get(entry);
                        // The row count is what tells an operator this is the live
                        // campaign and not one that ran once in August. "at least" when it
                        // came from a sample, because the next refresh may raise it.
                        // The workspace matters more than the row count when a client has
                        // several: "Aasha" and "Kiran" mean nothing beside each other
                        // without it — different teams doing different work.
                        const where = seen?.workspace ? `${seen.workspace} · ` : '';
                        const note = seen
                          ? ` — ${where}${seen.exact ? '' : 'at least '}${formatCount(seen.rows)} rows`
                          : ' — configured, not yet seen in an export';
                        return (
                          <option key={entry} value={entry}>
                            {entry}
                            {note}
                          </option>
                        );
                      })}
                      <option value={CUSTOM_FLOW}>Other…</option>
                    </Select>
                  ) : (
                    <Input
                      {...control}
                      mono
                      value={flow}
                      disabled={saving}
                      placeholder="(all flows)"
                      autoFocus={flowCustom}
                      onChange={(event) => setFlow(event.target.value)}
                    />
                  )
                }
              </Field>
              <Field
                label=" "
                hint={
                  client?.flows_refreshed_at
                    ? `Last read ${client.flows_refreshed_at.slice(0, 16).replace('T', ' ')} IST`
                    : 'Reads this client’s three most recent exports.'
                }
              >
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={saving || flowsRefreshing || !clientId}
                  onClick={refreshFlows}
                >
                  {flowsRefreshing ? 'Reading exports…' : 'Refresh flows'}
                </Button>
              </Field>
              <Field
                label="Campaign pattern"
                optional
                hint="Matched against the campaign name column. Blank means every campaign."
              >
                {(control) => (
                  <Input
                    {...control}
                    mono
                    value={campaign}
                    disabled={saving}
                    placeholder="(all campaigns)"
                    onChange={(event) => setCampaign(event.target.value)}
                  />
                )}
              </Field>
              <Field
                label="File format"
                hint={
                  shape === 'workbook'
                    ? 'A workbook is a workbook — the sheets carry live formulas, which no CSV has.'
                    : 'CSV streams; XLSX is easier for a recipient on Outlook.'
                }
              >
                {(control) => (
                  <Select
                    {...control}
                    value={shape === 'workbook' ? 'xlsx' : format}
                    disabled={saving || shape === 'workbook'}
                    onChange={(event) => setFormat(event.target.value as ReportFormat)}
                  >
                    <option value="csv">CSV</option>
                    <option value="xlsx">XLSX</option>
                  </Select>
                )}
              </Field>
            </div>
          </section>

          {submitError ? (
            <div className="callout tone-danger" role="alert">
              <span className="callout-glyph" aria-hidden="true">
                ✕
              </span>
              <span>{submitError}</span>
            </div>
          ) : null}
        </div>
      </Modal>

      {/* The one dialog that adds addresses, mounted here so the inline add carries the
          same right-party confirmation as the client's own Recipients card. Addresses are
          created on the CLIENT — this schedule only names ids. */}
      <AddRecipientsDialog
        open={addOpen}
        clientId={clientId}
        companyName={client?.company_name}
        clientName={client?.name}
        existing={recipients}
        onClose={() => setAddOpen(false)}
        onCreated={(created) => {
          setRecipients((current) => [...current, ...created]);
          setRecipientsKey((key) => key + 1);
          // Added from this form while picking: the operator plainly wants them on this
          // schedule, and leaving them unticked would look like the add had failed.
          if (recipientMode === 'pick') {
            setRecipientIds((current) => [
              ...current,
              ...created.map((row) => row.recipient_id).filter((id) => !current.includes(id)),
            ]);
          }
        }}
      />

      <TestMailDialog
        open={testOpen}
        schedule={schedule ?? null}
        client={client}
        onClose={() => setTestOpen(false)}
      />
    </>
  );
}

export default ScheduleEditor;
